import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./styles.css";
import API_BASE_URL from "./config";

function App() {
  const [sources, setSources] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [selectedSource, setSelectedSource] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("");
  const [selectedConnection, setSelectedConnection] = useState("");
  const [allConnectionData, setAllConnectionData] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [expandedMitrePhases, setExpandedMitrePhases] = useState({});
  const [expandedNistPhases, setExpandedNistPhases] = useState({});
  const [expandedFlow, setExpandedFlow] = useState(false);
  
  // Searchable dropdown states
  const [sourceSearch, setSourceSearch] = useState("");
  const [destinationSearch, setDestinationSearch] = useState("");
  const [connectionSearch, setConnectionSearch] = useState("");
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [showDestinationDropdown, setShowDestinationDropdown] = useState(false);
  const [showConnectionDropdown, setShowConnectionDropdown] = useState(false);
  
  // Keyboard navigation states
  const [sourceSelectedIndex, setSourceSelectedIndex] = useState(-1);
  const [destinationSelectedIndex, setDestinationSelectedIndex] = useState(-1);
  const [connectionSelectedIndex, setConnectionSelectedIndex] = useState(-1);
  
  // Error message state
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Auto-resize input function
  const autoResizeInput = (input, text) => {
    if (!input) return;
    
    // Create a temporary span to measure text width
    const span = document.createElement('span');
    span.style.visibility = 'hidden';
    span.style.position = 'absolute';
    span.style.fontSize = window.getComputedStyle(input).fontSize;
    span.style.fontFamily = window.getComputedStyle(input).fontFamily;
    span.style.fontWeight = window.getComputedStyle(input).fontWeight;
    span.style.padding = '0';
    span.style.border = '0';
    span.textContent = text || input.placeholder;
    
    document.body.appendChild(span);
    const textWidth = span.offsetWidth;
    document.body.removeChild(span);
    
    // Set width with some padding
    const newWidth = Math.max(150, textWidth + 40);
    input.style.width = `${newWidth}px`;
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/connections/sources`)
      .then((res) => res.json())
      .then(setSources)
      .catch(() => setSources([]));

    fetch(`${API_BASE_URL}/api/connections/destinations`)
      .then((res) => res.json())
      .then(setDestinations)
      .catch(() => setDestinations([]));

    // Fetch all connections directly from API
    fetch(`${API_BASE_URL}/api/connections/connections`)
      .then((res) => res.json())
      .then(setAllConnections)
      .catch(() => setAllConnections([]));
  }, []);

  useEffect(() => {
    if (selectedSource && selectedDestination) {
      fetch(`${API_BASE_URL}/api/connections/details?source=${selectedSource}&destination=${selectedDestination}`)
        .then((res) => res.json())
        .then((data) => {
          if (data && data.length > 0) {
            setAllConnectionData(data);
            setSelectedDetails(null);
            setSelectedConnection(""); // Reset connection selection when source/destination changes
            setShowErrorMessage(false); // Hide error message if data is found
          } else {
            // No data found for this combination
            setAllConnectionData([]);
            setSelectedDetails(null);
            setSelectedConnection("");
                   setErrorMessage(`Please select correct Source & Destination connecting the desired flow as shown in the figure below.\n\n(e.g., Alerting and Advisory System as Source and Emergency Management Center as Destination)`);
            setShowErrorMessage(true);
          }
        })
        .catch(() => {
          setAllConnectionData([]);
          setSelectedDetails(null);
          setSelectedConnection("");
          setErrorMessage(`Please select correct Source & Destination connecting the desired flow as shown in the figure below.\n\n(e.g., Alerting and Advisory System as Source and Emergency Management Center as Destination)`);
          setShowErrorMessage(true);
        });
    } else {
      // Clear connection data when source or destination is not selected
      setAllConnectionData([]);
      setSelectedDetails(null);
      setSelectedConnection("");
      setShowErrorMessage(false); // Hide error message when selections are cleared
    }
  }, [selectedSource, selectedDestination]);

  const uniqueConnections = Array.from(new Set(
    allConnectionData.map((conn) => conn.connection?.trim())
  )).filter(Boolean);

  // Determine which connections to show in the dropdown
  const getConnectionOptions = () => {
    if (selectedSource && selectedDestination) {
      // When both source and destination are selected, show filtered connections
      return uniqueConnections;
    } else {
      // When no source or destination is selected, show all connections from database
      return allConnections;
    }
  };

  const handleConnectionClick = (name) => {
    const matches = allConnectionData.filter((conn) => conn.connection?.trim() === name);
    setSelectedDetails(matches.length ? matches : null);
    setSelectedConnection(name);
  };

  const handleClear = () => {
    setSelectedSource("");
    setSelectedDestination("");
    setSelectedConnection("");
    setSelectedDetails(null);
    setAllConnectionData([]);
    setExpandedFlow(false);
    setExpandedMitrePhases({});
    setExpandedNistPhases({});
    // Clear search states
    setSourceSearch("");
    setDestinationSearch("");
    setConnectionSearch("");
    setShowSourceDropdown(false);
    setShowDestinationDropdown(false);
    setShowConnectionDropdown(false);
    // Clear keyboard navigation states
    setSourceSelectedIndex(-1);
    setDestinationSelectedIndex(-1);
    setConnectionSelectedIndex(-1);
    // Clear error message
    setShowErrorMessage(false);
    setErrorMessage("");
  };

  // Filter functions for searchable dropdowns
  const getFilteredSources = () => {
    return sources.filter(source => 
      source.toLowerCase().includes(sourceSearch.toLowerCase())
    );
  };

  const getFilteredDestinations = () => {
    return destinations.filter(destination => 
      destination.toLowerCase().includes(destinationSearch.toLowerCase())
    );
  };

  const getFilteredConnections = () => {
    const connections = getConnectionOptions();
    return connections.filter(connection => 
      connection.toLowerCase().includes(connectionSearch.toLowerCase())
    );
  };

  // Handlers for searchable dropdowns
  const handleSourceSelect = (source) => {
    setSelectedSource(source);
    setSourceSearch(source);
    setShowSourceDropdown(false);
    
    // Auto-resize the input
    setTimeout(() => {
      const sourceInput = document.querySelector('.source-destination-select[placeholder="Select Source"]');
      autoResizeInput(sourceInput, source);
    }, 0);
  };

  const handleDestinationSelect = (destination) => {
    setSelectedDestination(destination);
    setDestinationSearch(destination);
    setShowDestinationDropdown(false);
    
    // Auto-resize the input
    setTimeout(() => {
      const destinationInput = document.querySelector('.source-destination-select[placeholder="Select Destination"]');
      autoResizeInput(destinationInput, destination);
    }, 0);
  };

  const handleConnectionSelectFromDropdown = (connection) => {
    handleConnectionSelect(connection);
    setConnectionSearch(connection);
    setShowConnectionDropdown(false);
    
    // Auto-resize the input
    setTimeout(() => {
      const connectionInput = document.querySelector('.flow-connection-select[placeholder="Flow Connection"]');
      autoResizeInput(connectionInput, connection);
    }, 0);
  };

  // Keyboard navigation handlers
  const handleSourceKeyDown = (e) => {
    const filteredSources = getFilteredSources();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowSourceDropdown(true);
      const newIndex = sourceSelectedIndex < filteredSources.length - 1 ? sourceSelectedIndex + 1 : 0;
      setSourceSelectedIndex(newIndex);
      // Scroll selected option into view
      setTimeout(() => {
        const selectedElement = document.querySelector('.source-destination-options .dropdown-option.selected');
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setShowSourceDropdown(true);
      const newIndex = sourceSelectedIndex > 0 ? sourceSelectedIndex - 1 : filteredSources.length - 1;
      setSourceSelectedIndex(newIndex);
      // Scroll selected option into view
      setTimeout(() => {
        const selectedElement = document.querySelector('.source-destination-options .dropdown-option.selected');
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (sourceSelectedIndex >= 0 && filteredSources[sourceSelectedIndex]) {
        handleSourceSelect(filteredSources[sourceSelectedIndex]);
        setSourceSelectedIndex(-1);
      }
    } else if (e.key === 'Escape') {
      setShowSourceDropdown(false);
      setSourceSelectedIndex(-1);
    }
  };

  const handleDestinationKeyDown = (e) => {
    const filteredDestinations = getFilteredDestinations();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowDestinationDropdown(true);
      const newIndex = destinationSelectedIndex < filteredDestinations.length - 1 ? destinationSelectedIndex + 1 : 0;
      setDestinationSelectedIndex(newIndex);
      // Scroll selected option into view
      setTimeout(() => {
        const selectedElement = document.querySelector('.source-destination-options .dropdown-option.selected');
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setShowDestinationDropdown(true);
      const newIndex = destinationSelectedIndex > 0 ? destinationSelectedIndex - 1 : filteredDestinations.length - 1;
      setDestinationSelectedIndex(newIndex);
      // Scroll selected option into view
      setTimeout(() => {
        const selectedElement = document.querySelector('.source-destination-options .dropdown-option.selected');
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (destinationSelectedIndex >= 0 && filteredDestinations[destinationSelectedIndex]) {
        handleDestinationSelect(filteredDestinations[destinationSelectedIndex]);
        setDestinationSelectedIndex(-1);
      }
    } else if (e.key === 'Escape') {
      setShowDestinationDropdown(false);
      setDestinationSelectedIndex(-1);
    }
  };

  const handleConnectionKeyDown = (e) => {
    const filteredConnections = getFilteredConnections();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowConnectionDropdown(true);
      const newIndex = connectionSelectedIndex < filteredConnections.length - 1 ? connectionSelectedIndex + 1 : 0;
      setConnectionSelectedIndex(newIndex);
      // Scroll selected option into view
      setTimeout(() => {
        const selectedElement = document.querySelector('.flow-connection-options .dropdown-option.selected');
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setShowConnectionDropdown(true);
      const newIndex = connectionSelectedIndex > 0 ? connectionSelectedIndex - 1 : filteredConnections.length - 1;
      setConnectionSelectedIndex(newIndex);
      // Scroll selected option into view
      setTimeout(() => {
        const selectedElement = document.querySelector('.flow-connection-options .dropdown-option.selected');
        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (connectionSelectedIndex >= 0 && filteredConnections[connectionSelectedIndex]) {
        handleConnectionSelectFromDropdown(filteredConnections[connectionSelectedIndex]);
        setConnectionSelectedIndex(-1);
      }
    } else if (e.key === 'Escape') {
      setShowConnectionDropdown(false);
      setConnectionSelectedIndex(-1);
    }
  };

  const handleConnectionSelect = (connection) => {
    setSelectedConnection(connection);
    if (connection) {
      if (selectedSource && selectedDestination) {
        // When both source and destination are selected, filter by them
        const matches = allConnectionData.filter((conn) => conn.connection?.trim() === connection);
        setSelectedDetails(matches.length ? matches : null);
      } else {
        // When no source or destination is selected, we need to find the data for this connection
        // This will require fetching data for all source-destination pairs that contain this connection
        findConnectionData(connection);
      }
    } else {
      setSelectedDetails(null);
    }
  };

  const findConnectionData = async (connectionName) => {
    try {
      // Single API call to fetch all records matching this connection
      const res = await fetch(`${API_BASE_URL}/api/connections/by-connection?connection=${encodeURIComponent(connectionName)}`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setSelectedDetails(data);
        } else {
          setSelectedDetails(null);
        }
      } else {
        setSelectedDetails(null);
      }
    } catch (error) {
      console.error('Failed to find connection data:', error);
      setSelectedDetails(null);
    }
  };

  const toggleMitrePhase = (phase) => {
    setExpandedMitrePhases((prev) => ({ ...prev, [phase]: !prev[phase] }));
  };

  const toggleNistPhase = (phase) => {
    setExpandedNistPhases((prev) => ({ ...prev, [phase]: !prev[phase] }));
  };

  const toggleFlow = () => {
    setExpandedFlow((prev) => !prev);
  };

  return (
    <div className="container">
      {/* Removed old Home button, now handled by Navbar */}
      <div style={{ position: 'relative' }}>
        <Link to="/" className="go-to-home-link">Go to home</Link>
        <h1 className="title">Transit Security Service Package</h1>
      </div>

      <div className="transit-description">
        <h2>Transit Security</h2>
        <p>
          Transit Security encompasses the protection of transportation systems, infrastructure, and passengers from various threats including cyber attacks, physical security breaches, and operational disruptions. This comprehensive approach integrates advanced technologies, surveillance systems, access controls, and emergency response protocols to ensure safe and secure transit operations across multiple transportation modes.
        </p>
      </div>

      <img src="/transit.png" alt="Transit Security Diagram" className="transit-img-large" />

      <div className="transit-card">
        <div className="instruction-header">
          <p className="dropdown-instruction">To explore possible cyberthreats to information flows and find recommended applicable security controls and mitigation measures based on <strong>MITRE Attack</strong> and <strong>NIST Frameworks</strong>, select from the below search options:</p>
        </div>
        <div className="dropdowns-container">
          <div className="dropdowns-header">
            <span className="dropdowns-label">Select Your Parameters</span>
            <p className="selection-hint">Choose <strong>Source & Destination</strong> OR <strong>Flow Connection</strong></p>
          </div>
          
          {/* Source & Destination Group */}
          <div className="dropdown-group source-destination-group">
            <div className="group-header">
              <span className="group-title">📍 Source & Destination Analysis</span>
              <p className="group-example">(e.g., Alerting and Advisory System as Source and Emergency Management Center as Destination)</p>
            </div>
            <div className="group-dropdowns">
              {/* Searchable Source Dropdown */}
              <div className="searchable-dropdown">
                <input
                  type="text"
                  value={sourceSearch}
                  onChange={(e) => {
                    setSourceSearch(e.target.value);
                    setSourceSelectedIndex(-1);
                  }}
                  onFocus={() => setShowSourceDropdown(true)}
                  onBlur={() => setTimeout(() => setShowSourceDropdown(false), 200)}
                  onKeyDown={handleSourceKeyDown}
                  placeholder="Select Source"
                  className="styled-select source-destination-select"
                />
                {showSourceDropdown && (
                  <div className="dropdown-options source-destination-options">
                    {getFilteredSources().map((source, index) => (
                      <div
                        key={source}
                        className={`dropdown-option source-destination-option ${
                          index === sourceSelectedIndex ? 'selected' : ''
                        }`}
                        onClick={() => handleSourceSelect(source)}
                      >
                        {source}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Searchable Destination Dropdown */}
              <div className="searchable-dropdown">
                <input
                  type="text"
                  value={destinationSearch}
                  onChange={(e) => {
                    setDestinationSearch(e.target.value);
                    setDestinationSelectedIndex(-1);
                  }}
                  onFocus={() => setShowDestinationDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDestinationDropdown(false), 200)}
                  onKeyDown={handleDestinationKeyDown}
                  placeholder="Select Destination"
                  className="styled-select source-destination-select"
                />
                {showDestinationDropdown && (
                  <div className="dropdown-options source-destination-options">
                    {getFilteredDestinations().map((destination, index) => (
                      <div
                        key={destination}
                        className={`dropdown-option source-destination-option ${
                          index === destinationSelectedIndex ? 'selected' : ''
                        }`}
                        onClick={() => handleDestinationSelect(destination)}
                      >
                        {destination}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* OR Separator */}
          <div className="or-separator">
            <span className="or-text">OR</span>
          </div>

          {/* Flow Connection Group */}
          <div className="dropdown-group flow-connection-group">
            <div className="group-header">
              <span className="group-title">🔗 Flow Connection Analysis</span>
            </div>
            <div className="group-dropdowns">
              {/* Searchable Connection Dropdown */}
              <div className="searchable-dropdown">
                <input
                  type="text"
                  value={connectionSearch}
                  onChange={(e) => {
                    setConnectionSearch(e.target.value);
                    setConnectionSelectedIndex(-1);
                  }}
                  onFocus={() => setShowConnectionDropdown(true)}
                  onBlur={() => setTimeout(() => setShowConnectionDropdown(false), 200)}
                  onKeyDown={handleConnectionKeyDown}
                  placeholder="Flow Connection"
                  className="styled-select flow-connection-select"
                />
                {showConnectionDropdown && (
                  <div className="dropdown-options flow-connection-options">
                    {getFilteredConnections().map((connection, index) => (
                      <div
                        key={connection}
                        className={`dropdown-option flow-connection-option ${
                          index === connectionSelectedIndex ? 'selected' : ''
                        }`}
                        onClick={() => handleConnectionSelectFromDropdown(connection)}
                      >
                        {connection}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clear Button */}
          <div className="clear-button-container">
            <button onClick={handleClear} className="clear-button">
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Error Message Notification */}
      {showErrorMessage && (
        <div className="error-popup">
          <div className="error-popup-content">
            <div className="error-icon">⚠️</div>
            <div className="error-message">{errorMessage}</div>
            <button 
              className="error-confirm-btn" 
              onClick={() => {
                handleClear();
                setShowErrorMessage(false);
              }}
            >
              Clear & Try Again
            </button>
          </div>
        </div>
      )}

      {/* Show buttons and details when we have connection data or selected details */}
      {((allConnectionData && allConnectionData.length > 0) || (selectedDetails && selectedDetails.length > 0)) && (
        <div className="details">
          {/* Selection Header */}
          <div className="selection-header">
            <h3 className="selection-title">
              <div className="selection-details">
                {selectedSource && (
                  <div><span className="selection-label">Source:</span> {selectedSource}</div>
                )}
                {!selectedSource && selectedDetails && selectedDetails.length > 0 && selectedDetails[0].source && (
                  <div><span className="selection-label">Source:</span> {selectedDetails[0].source}</div>
                )}
                {selectedDestination && (
                  <div><span className="selection-label">Destination:</span> {selectedDestination}</div>
                )}
                {!selectedDestination && selectedDetails && selectedDetails.length > 0 && selectedDetails[0].destination && (
                  <div><span className="selection-label">Destination:</span> {selectedDetails[0].destination}</div>
                )}
              </div>
            </h3>
          </div>

          {/* Connection Options/Output Buttons */}
          {uniqueConnections && uniqueConnections.length > 0 && (
            <div className="connection-list">
              {uniqueConnections.map((connName, idx) => (
                <button 
                  key={idx} 
                  className={`connection-button ${selectedConnection === connName || (selectedDetails && selectedDetails.length > 0 && selectedDetails[0].connection?.trim() === connName) ? 'selected' : ''}`}
                  onClick={() => handleConnectionClick(connName)}
                >
                  {connName}
                </button>
              ))}
            </div>
          )}

          {selectedDetails && selectedDetails.length > 0 && (
            <>
                <button onClick={toggleFlow} className="collapsible">
                  {expandedFlow ? "▼" : "▶"} Flow Details and Characteristics
                  <a 
                    href="/cybersecurity-glossary" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      marginLeft: '15px', 
                      fontSize: '0.9rem', 
                      color: '#ffffff', 
                      backgroundColor: '#3b82f6',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      textDecoration: 'none',
                      fontWeight: '600',
                      boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#2563eb';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#3b82f6';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.3)';
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    📖 View Definitions
                  </a>
                </button>
          {expandedFlow && (
            <div className="flow-section">
              <p><strong>Flow Time Context:</strong> {selectedDetails[0].charTimeContext || "—"}</p>
              <p><strong>Flow Spatial Context:</strong> {selectedDetails[0].charSpatialContext || "—"}</p>
              <p><strong>Flow Cardinality:</strong> {selectedDetails[0].charCardinality || "—"}</p>
              <p><strong>Flow Control:</strong> {selectedDetails[0].charControl || "—"}</p>
              <p><strong>Flow Security:</strong> {selectedDetails[0].charSecurity || "—"}</p>
              <p><strong>Initiator:</strong> {selectedDetails[0].charInitiator || "—"}</p>
              <p><strong>Information Flow:</strong> {selectedDetails[0].informationFlow || "—"}</p>
              <p><strong>Equipments:</strong> {selectedDetails[0].equipments || "—"}</p>
              <p><strong>Flow Confidentiality:</strong> {selectedDetails[0].secConfidentiality || "—"}</p>
              <p><strong>Flow Integrity:</strong> {selectedDetails[0].secIntegrity || "—"}</p>
              <p><strong>Flow Availability:</strong> {selectedDetails[0].secAvailability || "—"}</p>
            </div>
          )}

                <h2>MITRE Attack Techniques & Mitigation Measures</h2>
          {[...new Set(selectedDetails.map((d) => d.attackPhase))].map((phase) => (
            <div key={phase} className="attack-phase">
              <button onClick={() => toggleMitrePhase(phase)} className="collapsible">
                {expandedMitrePhases[phase] ? "▼" : "▶"} {phase} Phase
              </button>
              {expandedMitrePhases[phase] && (
                <div className="technique-group">
                  <div className="subsection">
                    {selectedDetails
                      .filter((d) => d.attackPhase === phase)
                      .map((d, i) => (
                        <div key={i} className="card">
                          <p><strong>Attack Technique:</strong> {d.attackTechnique}</p>
                          <p><strong>MITRE Description:</strong> {d.mitreDescription}</p>
                          <p><strong>MITRE Mitigation:</strong> {d.mitreMitigationTechnique}</p>
                          <p><strong>MITRE Mitigation Description:</strong> {d.mitreMitigationDescription}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          <h2>NIST Mitigation & Controls Description</h2>
          {[...new Set(selectedDetails.map((d) => d.attackPhase))].map((phase) => (
            <div key={`nist-${phase}`} className="attack-phase">
              <button onClick={() => toggleNistPhase(phase)} className="collapsible">
                {expandedNistPhases[phase] ? "▼" : "▶"} {phase} Phase
              </button>
              {expandedNistPhases[phase] && (
                <div className="technique-group">
                  <div className="subsection">
                    {selectedDetails
                      .filter((d) => d.attackPhase === phase)
                      .map((d, i) => (
                        <div key={i} className="card">
                          <p><strong>NIST Mitigation & Controls:</strong> {d.nistMitigationTechnique}</p>
                          <p><strong>NIST Mitigation Description:</strong> {d.nistMitigationDescription}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ))}
            </>
          )}
          {!selectedDetails || selectedDetails.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
              <p>Select a connection option above to view details</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default App;